import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  Snackbar,
  Divider,
  Tooltip,
  IconButton,
} from '@mui/material';
import {
  ContentCopy as CopyIcon,
  PeopleAlt as PeopleIcon,
  MonetizationOn as EarnIcon,
  TouchApp as ClickIcon,
  HowToReg as SignupIcon,
  CheckCircle as ConvertIcon,
} from '@mui/icons-material';
import { apiClient } from '../../api/client';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface AffiliateProfile {
  id: string;
  user_id: string;
  email: string;
  name: string;
  payout_email: string;
  referral_code: string;
  status: string;
  total_clicks: number;
  total_signups: number;
  total_conversions: number;
  total_earned: number;
  created_at: string;
  referral_link: string;
}

interface ReferralRow {
  id: string;
  referral_code: string;
  referred_email: string | null;
  referred_user_id: string | null;
  event_type: string;
  commission_amount: number;
  status: string;
  created_at: string;
  converted_at: string | null;
}

// ---------------------------------------------------------------------------
// Stat card sub-component
// ---------------------------------------------------------------------------

interface StatCardProps {
  label: string;
  value: string | number;
  icon: React.ReactElement;
  color: string;
}

const StatCard: React.FC<StatCardProps> = ({ label, value, icon, color }) => (
  <Card>
    <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
      <Box
        sx={{
          width: 48,
          height: 48,
          borderRadius: '50%',
          bgcolor: `${color}20`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color,
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>
      <Box>
        <Typography variant="h5" fontWeight={700} color={color}>
          {value}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {label}
        </Typography>
      </Box>
    </CardContent>
  </Card>
);

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

const ReferralPage: React.FC = () => {
  const [profile, setProfile] = useState<AffiliateProfile | null>(null);
  const [referrals, setReferrals] = useState<ReferralRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [registerLoading, setRegisterLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState('');

  // Registration form state
  const [formName, setFormName] = useState('');
  const [formPayoutEmail, setFormPayoutEmail] = useState('');
  const [formError, setFormError] = useState('');

  // ---------------------------------------------------------------------------
  // Data fetching
  // ---------------------------------------------------------------------------

  const fetchProfile = useCallback(async () => {
    try {
      const res = await apiClient.get<AffiliateProfile>('/affiliates/me');
      setProfile(res.data);
    } catch (err: any) {
      if (err.message === 'Resource not found') {
        // Not registered yet - that is fine
        setProfile(null);
      } else {
        setError(err.message || 'Failed to load affiliate profile.');
      }
    }
  }, []);

  const fetchReferrals = useCallback(async () => {
    try {
      const res = await apiClient.get<ReferralRow[]>('/affiliates/referrals');
      setReferrals(res.data);
    } catch {
      // Silently ignore - referrals table may be empty
    }
  }, []);

  useEffect(() => {
    setLoading(true);
    Promise.all([fetchProfile()]).finally(() => setLoading(false));
  }, [fetchProfile]);

  useEffect(() => {
    if (profile) {
      fetchReferrals();
    }
  }, [profile, fetchReferrals]);

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');
    if (!formName.trim()) {
      setFormError('Name is required.');
      return;
    }
    if (!formPayoutEmail.trim()) {
      setFormError('Payout email is required.');
      return;
    }
    setRegisterLoading(true);
    try {
      const res = await apiClient.post<AffiliateProfile>('/affiliates/register', {
        name: formName.trim(),
        payout_email: formPayoutEmail.trim(),
      });
      setProfile(res.data);
      setSnackbar('You are now registered as an affiliate!');
    } catch (err: any) {
      setFormError(err.message || 'Registration failed.');
    } finally {
      setRegisterLoading(false);
    }
  };

  const handleCopy = () => {
    if (!profile) return;
    navigator.clipboard.writeText(profile.referral_link).then(() => {
      setSnackbar('Referral link copied to clipboard!');
    });
  };

  // ---------------------------------------------------------------------------
  // Status chip helper
  // ---------------------------------------------------------------------------

  const statusColor = (s: string): 'default' | 'success' | 'warning' | 'error' => {
    if (s === 'confirmed') return 'success';
    if (s === 'pending') return 'warning';
    if (s === 'rejected') return 'error';
    return 'default';
  };

  const eventLabel = (e: string) => {
    if (e === 'click') return 'Click';
    if (e === 'signup') return 'Signup';
    if (e === 'conversion') return 'Conversion';
    return e;
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  // ---- Not registered -------------------------------------------------------
  if (!profile) {
    return (
      <Box sx={{ p: 3, maxWidth: 520, mx: 'auto', mt: 4 }}>
        <Typography variant="h4" gutterBottom>
          Join the Referral Program
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Earn commissions by referring new users to Executive Force CapFlow.
          Fill in the form below to get your unique referral link.
        </Typography>

        <Card>
          <CardContent>
            <Box component="form" onSubmit={handleRegister} noValidate>
              <TextField
                label="Your Name"
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
                fullWidth
                required
                sx={{ mb: 2 }}
              />
              <TextField
                label="Payout Email"
                type="email"
                value={formPayoutEmail}
                onChange={(e) => setFormPayoutEmail(e.target.value)}
                fullWidth
                required
                helperText="Earnings will be sent to this email via PayPal / bank transfer."
                sx={{ mb: 2 }}
              />
              {formError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {formError}
                </Alert>
              )}
              <Button
                type="submit"
                variant="contained"
                size="large"
                fullWidth
                disabled={registerLoading}
                startIcon={registerLoading ? <CircularProgress size={18} /> : <PeopleIcon />}
              >
                {registerLoading ? 'Registering…' : 'Join the Program'}
              </Button>
            </Box>
          </CardContent>
        </Card>

        <Snackbar
          open={!!snackbar}
          autoHideDuration={4000}
          onClose={() => setSnackbar('')}
          message={snackbar}
        />
      </Box>
    );
  }

  // ---- Registered -----------------------------------------------------------
  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Your Referral Dashboard
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Share your unique link and earn rewards for every new user you bring in.
      </Typography>

      {/* Referral link card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            Your Referral Link
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <TextField
              value={profile.referral_link}
              InputProps={{ readOnly: true }}
              size="small"
              sx={{ flexGrow: 1, minWidth: 240 }}
            />
            <Tooltip title="Copy to clipboard">
              <IconButton onClick={handleCopy} color="primary">
                <CopyIcon />
              </IconButton>
            </Tooltip>
            <Button
              variant="contained"
              onClick={handleCopy}
              startIcon={<CopyIcon />}
              size="small"
            >
              Copy Link
            </Button>
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Code: <strong>{profile.referral_code}</strong> &nbsp;|&nbsp; Status:{' '}
            <Chip label={profile.status} size="small" color={profile.status === 'active' ? 'success' : 'default'} />
          </Typography>
        </CardContent>
      </Card>

      {/* Stat cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            label="Total Clicks"
            value={profile.total_clicks}
            icon={<ClickIcon />}
            color="#1976d2"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            label="Signups"
            value={profile.total_signups}
            icon={<SignupIcon />}
            color="#7b1fa2"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            label="Conversions"
            value={profile.total_conversions}
            icon={<ConvertIcon />}
            color="#2e7d32"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            label="Earned"
            value={`$${profile.total_earned.toFixed(2)}`}
            icon={<EarnIcon />}
            color="#e65100"
          />
        </Grid>
      </Grid>

      <Divider sx={{ mb: 3 }} />

      {/* Referrals table */}
      <Typography variant="h6" gutterBottom>
        Referral Events
      </Typography>

      {referrals.length === 0 ? (
        <Alert severity="info">
          No referral events yet. Share your link to start earning!
        </Alert>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Event</TableCell>
                <TableCell>Referred Email</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Commission</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {referrals.map((row) => (
                <TableRow key={row.id} hover>
                  <TableCell>
                    {new Date(row.created_at).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                    })}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={eventLabel(row.event_type)}
                      size="small"
                      color={
                        row.event_type === 'conversion'
                          ? 'success'
                          : row.event_type === 'signup'
                          ? 'primary'
                          : 'default'
                      }
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>{row.referred_email || '—'}</TableCell>
                  <TableCell>
                    <Chip
                      label={row.status}
                      size="small"
                      color={statusColor(row.status)}
                    />
                  </TableCell>
                  <TableCell align="right">
                    {row.commission_amount > 0
                      ? `$${row.commission_amount.toFixed(2)}`
                      : '—'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Snackbar
        open={!!snackbar}
        autoHideDuration={4000}
        onClose={() => setSnackbar('')}
        message={snackbar}
      />
    </Box>
  );
};

export default ReferralPage;
